/*
 * Carbisdale - MarkdownBlockParser
 *
 * Parst Markdown-Text in Block-Level-Elemente.
 * Pure Functions, wiederverwendbar fuer Console und Editor.
 *
 * Block-Typen:
 *   paragraph   - Normaler Text (aufeinanderfolgende Zeilen gemergt)
 *   heading     - # bis ### (Marker entfernt, WYSIWYG)
 *   list_item   - Bullet (- / *) oder nummeriert (1. 2.), verschachtelt
 *   code        - Fenced Code Block (``` ... ```)
 *   blockquote  - > Zitat (aufeinanderfolgende > Zeilen gemergt)
 */

/**
 * Parst einen Markdown-Text in semantische Bloecke.
 * Streaming-tolerant: ungeschlossene Code-Bloecke werden als Code behandelt.
 *
 * @param {string} text - Markdown-Text (z.B. AI-Antwort)
 * @returns {Array<{type: string, text: string, level?: number, indent?: number, bullet?: string}>}
 */
export function parseMarkdownBlocks(text) {
    if (!text) return [{ type: 'paragraph', text: '' }]

    const lines = text.split('\n')
    const blocks = []
    let inCodeBlock = false
    let codeLines = []
    let paraLines = []
    let lastWasBreak = true  // Am Anfang: kein Blockquote-Merge

    function flushParagraph() {
        if (paraLines.length === 0) return
        blocks.push({ type: 'paragraph', text: paraLines.join(' ') })
        paraLines = []
    }

    for (let i = 0; i < lines.length; i++) {
        const line = lines[i]

        // === Code-Block-Modus ===
        if (inCodeBlock) {
            if (/^\s*```/.test(line)) {
                // Code-Block schliessen
                blocks.push({ type: 'code', text: codeLines.join('\n') })
                codeLines = []
                inCodeBlock = false
                lastWasBreak = true
            } else {
                codeLines.push(line)
            }
            continue
        }

        // === Code-Block oeffnen ===
        if (/^\s*```/.test(line)) {
            flushParagraph()
            inCodeBlock = true
            lastWasBreak = false
            continue
        }

        // === Leerzeile ===
        if (line.trim() === '') {
            flushParagraph()
            lastWasBreak = true
            continue
        }

        // === Horizontal Rule (---, ***, ___) ===
        if (/^[-*_]{3,}\s*$/.test(line)) {
            flushParagraph()
            blocks.push({ type: 'hr', text: '' })
            lastWasBreak = true
            continue
        }

        // === Heading (# bis ###) ===
        const headingMatch = line.match(/^(#{1,3})\s+(.+)/)
        if (headingMatch) {
            flushParagraph()
            blocks.push({
                type: 'heading',
                level: headingMatch[1].length,
                text: headingMatch[2]
            })
            lastWasBreak = true
            continue
        }

        // === Bullet-Liste (- oder *, mit optionaler Einrueckung) ===
        const bulletMatch = line.match(/^(\s*)([-*])\s+(.+)/)
        if (bulletMatch) {
            flushParagraph()
            blocks.push({
                type: 'list_item',
                indent: Math.floor(bulletMatch[1].length / 2),
                bullet: '\u2022',
                text: bulletMatch[3]
            })
            lastWasBreak = false
            continue
        }

        // === Nummerierte Liste (1. oder 1.2. etc.) ===
        const numberedMatch = line.match(/^(\s*)(\d+(?:\.\d+)*)\.\s+(.+)/)
        if (numberedMatch) {
            flushParagraph()
            blocks.push({
                type: 'list_item',
                indent: Math.floor(numberedMatch[1].length / 2),
                bullet: numberedMatch[2] + '.',
                text: numberedMatch[3]
            })
            lastWasBreak = false
            continue
        }

        // === Blockquote (> ...) ===
        const quoteMatch = line.match(/^>\s?(.*)/)
        if (quoteMatch) {
            flushParagraph()
            // Aufeinanderfolgende Blockquotes mergen (nur ohne Leerzeile dazwischen)
            const last = blocks.length > 0 ? blocks[blocks.length - 1] : null
            if (last && last.type === 'blockquote' && !lastWasBreak) {
                last.text += ' ' + quoteMatch[1]
            } else {
                blocks.push({ type: 'blockquote', text: quoteMatch[1] })
            }
            lastWasBreak = false
            continue
        }

        // === Tabellen-Zeile (| ... |) → als Code-Block rendern (monospace, zeilenweise) ===
        if (line.trimStart().startsWith('|')) {
            flushParagraph()
            // Separator-Zeilen (|---|---|) überspringen
            if (/^\s*\|[\s:|-]+\|\s*$/.test(line)) {
                lastWasBreak = false
                continue
            }
            // Aufeinanderfolgende Tabellen-Zeilen mergen
            const last = blocks.length > 0 ? blocks[blocks.length - 1] : null
            if (last && last.type === 'code' && last._isTable) {
                last.text += '\n' + line.trim()
            } else {
                const block = { type: 'code', text: line.trim(), _isTable: true }
                blocks.push(block)
            }
            lastWasBreak = false
            continue
        }

        // === Normaler Text → Paragraph (aufeinanderfolgende Zeilen mergen) ===
        paraLines.push(line)
        lastWasBreak = false
    }

    // Restliche Inhalte flushen
    if (inCodeBlock) {
        // Streaming-Toleranz: ungeschlossener Code-Block
        blocks.push({ type: 'code', text: codeLines.join('\n') })
    }
    flushParagraph()

    if (blocks.length === 0) {
        blocks.push({ type: 'paragraph', text: '' })
    }

    return blocks
}
